/**
 * RM24 staff in the client portal choose the client they act for (Marti
 * 2026-10-22): an admin enters an order for somebody who called in and is not
 * a portal member. Every client screen then works for that client exactly as
 * it does for its own people — same order form, same business rules — except
 * that staff may book sooner than the client's lead time.
 *
 * A new client is created first in the admin's own client list; this only
 * picks among existing ones.
 */
import { Alert, Autocomplete, Button, Stack, TextField } from '@mui/material';
import type { Client } from '@rendimees24/shared-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useActiveCompany } from '../client/ActiveCompanyContext';
import { subscribeClients } from '../services/clients';

export function StaffActingClientBar() {
  const { t } = useTranslation('client');
  const navigate = useNavigate();
  const { actingAsStaff, clientId, setActingClient } = useActiveCompany();
  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    if (!actingAsStaff) return;
    return subscribeClients(setClients);
  }, [actingAsStaff]);

  if (!actingAsStaff) return null;
  const selected = clients.find((c) => c.id === clientId) ?? null;

  return (
    <Alert severity="info" icon={false} sx={{ mb: 2 }} data-testid="staff-acting-bar">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 280, flex: 1 }}
          options={clients}
          value={selected}
          onChange={(_, c) => setActingClient(c?.id ?? null)}
          getOptionLabel={(c) => c.companyName ?? c.id}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label={t('staffActing.label')} />}
        />
        <Button
          variant="contained"
          disabled={!clientId}
          onClick={() => navigate('/client/new-order')}
        >
          {t('staffActing.newOrder')}
        </Button>
        <Button variant="text" onClick={() => navigate('/clients')}>
          {t('staffActing.newClient')}
        </Button>
      </Stack>
    </Alert>
  );
}
